import {
  Alert,
  AlertTitle,
  Box,
  Divider,
  Grid,
  GridLegacy,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import {
  HookFormDateDurationInput,
  HookFormInput,
  HookFormTableInput,
} from 'ui-component/HookFormComponents';
import { OrgPartFormConstants } from 'ui-component/CreateNewOrgPartDialog/constants';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { ALL_APPS } from 'constants/appConstants';
import { ChangeEvent } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import PartDetailsForm from 'ui-component/CreateNewOrgPartDialog/components/PartDetailsForm';
import { useSelector } from 'store';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import useFindParts from 'ui-component/CreateNewOrgPartDialog/useFindParts';
import ManualPartDetails from 'ui-component/CreateNewOrgPartDialog/components/ManualPartDetails';
import { OrgPartType } from 'types/clientV2/parts';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import {
  IconExternalLink,
  IconInfinity,
  IconSparkles,
} from '@tabler/icons-react';
import HookFormToggleAlert from 'ui-component/HookFormComponents/HookFormToggleAlert';
import AccordionCard from 'ui-component/cards/AccordionCard';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { quoteLineFormConstants } from 'ui-component/supplierQuotes/QuoteLineForm';
import { appPermissionAccessLevels } from 'types/apps';
import HookFormOrgSupplierAutocomplete from 'ui-component/HookFormComponents/HookFormOrgSupplierAutocomplete';
import { UNLIMITED_QUANTITY_STRING } from 'constants/procurementConstants';
import _ from 'lodash';
import { useTheme } from '@mui/material/styles';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import { HookFormNumberInput } from 'ui-component/HookFormComponents/HookFormNumberInput';
import { HookFormUnitOfMeasureAutocomplete } from 'ui-component/HookFormComponents/HookFormUnitOfMeasureAutocomplete';
import {
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

export const PaddedDivider = () => (
  <Box sx={{ pt: 1 }}>
    <Divider />
  </Box>
);

type CreatePartFormProps = {
  formRenderMode: OrgPartType;
  setHeroImage: (file: File | null) => void;
  hideLink?: boolean;
};

const CreatePartForm = ({
  formRenderMode,
  setHeroImage,
  hideLink = false,
}: CreatePartFormProps) => {
  const tempUoM = useFeature(FeatureFlags.tempUoM).on;
  const theme = useTheme();
  const { isPermissionDisabled } = useAppAccessContext();
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const canCreateSupplierQuote = !isPermissionDisabled({
    app: ALL_APPS.SOURCING.id,
    accessLevel: appPermissionAccessLevels.edit,
  });

  const {
    foundCustomPart,
    foundPublicPart,
    isSuccessSearchPublicPart,
    isFetchingSearchPublicPart,
    searchPublicPart,
  } = useFindParts();

  const {
    control,
    formState: { errors },
    setValue,
    register,
  } = useFormContext();

  const watchLinkPart = useWatch({
    control,
    name: OrgPartFormConstants.linkMatchedPart.id,
  });

  const watchSupplierQuoteOpen = useWatch({
    control,
    name: OrgPartFormConstants.supplierQuoteOpen.id,
  });

  const noFoundPublicPart =
    isSuccessSearchPublicPart &&
    !isFetchingSearchPublicPart &&
    !foundPublicPart;

  const partTypeNotLinkable = [
    OrgPartType.CUSTOM,
    OrgPartType.SERVICE,
  ].includes(formRenderMode);

  const unlinkedOTSPart = formRenderMode === OrgPartType.OTS && !watchLinkPart;

  const watchScrapedFields = useWatch({
    control,
    name: OrgPartFormConstants.scrapedFields.id,
  });

  const watchDimensionClass = useWatch({
    control,
    name: quoteLineFormConstants.dimensionClass.id,
  });

  const watchDimensionUnit = useWatch({
    control,
    name: quoteLineFormConstants.dimensionUnit.id,
  });

  const watchDimensionValue = useWatch({
    control,
    name: quoteLineFormConstants.dimensionValue.id,
  });

  return (
    <>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <GridLegacy item xs={formRenderMode === OrgPartType.CUSTOM ? 8 : 12}>
            <HookFormInput
              control={control}
              errors={errors}
              name={OrgPartFormConstants.orgPartNumber.id}
              label={`${org?.name ?? 'Custom'} ${
                OrgPartFormConstants.orgPartNumber.label
              }`}
              boxSx={{ mb: 0 }}
            />
          </GridLegacy>
          {formRenderMode === OrgPartType.CUSTOM && (
            <GridLegacy item xs={4}>
              <HookFormInput
                control={control}
                errors={errors}
                name={OrgPartFormConstants.revision.id}
                label={OrgPartFormConstants.revision.label}
                boxSx={{ mb: 0 }}
              />
            </GridLegacy>
          )}
        </Stack>
        {foundCustomPart && (
          <Alert
            severity="warning"
            action={
              <SegmentButton
                color="inherit"
                size="small"
                onClick={() =>
                  window.open(
                    `${ALL_APPS.PARTS.path}/${foundCustomPart.id}`,
                    '_blank'
                  )
                }
              >
                View Existing Part
              </SegmentButton>
            }
          >
            <AlertTitle>Duplicate {org?.name} Part Number</AlertTitle>
            There is already a part in your library with the {org?.name} Part
            Number of {foundCustomPart.customId}.
            <br />
            You can intentionally assign the same {org?.name} Part Number to
            multiple interchangeable part records to group them together, but
            please ensure that this is your intention.
          </Alert>
        )}
        <PaddedDivider />
        <PartDetailsForm
          isManualEntry={
            formRenderMode === OrgPartType.OTS ? !watchLinkPart : true
          }
          formRenderMode={formRenderMode}
          setHeroImage={setHeroImage}
          searchPublicPart={searchPublicPart}
          hideLink={hideLink}
        />
        {(noFoundPublicPart || partTypeNotLinkable || unlinkedOTSPart) && (
          <ManualPartDetails
            onlyRenderClassification={formRenderMode === OrgPartType.SERVICE}
          />
        )}
        {canCreateSupplierQuote && (
          <AccordionCard
            title={
              <Stack direction="row" alignItems="start" spacing={1}>
                {_.includes(
                  watchScrapedFields,
                  OrgPartFormConstants.prices.id
                ) && (
                  <IconSparkles size={20} color={theme.palette.success.main} />
                )}
                <Typography
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Supplier Quote Pricing
                </Typography>
              </Stack>
            }
            expanded={watchSupplierQuoteOpen}
            onChange={(expanded) =>
              setValue(OrgPartFormConstants.supplierQuoteOpen.id, expanded)
            }
          >
            <Stack spacing={2}>
              <HookFormOrgSupplierAutocomplete
                name={OrgPartFormConstants.orgSupplier.id}
                label={OrgPartFormConstants.orgSupplier.label}
                setValue={setValue}
                control={control}
                errors={errors}
                mt={0}
              />
              <HookFormDateDurationInput
                name={OrgPartFormConstants.shipsInLead.id}
                label={OrgPartFormConstants.shipsInLead.label}
                durationLabel={OrgPartFormConstants.shipsInLead.label}
                businessDays
                control={control}
                setValue={setValue}
              />
              <HookFormInput
                name={OrgPartFormConstants.maximum.id}
                label={OrgPartFormConstants.maximum.label}
                control={control}
                errors={errors}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SegmentIconButton
                        onClick={() =>
                          setValue(
                            OrgPartFormConstants.maximum.id,
                            UNLIMITED_QUANTITY_STRING
                          )
                        }
                      >
                        <IconInfinity />
                      </SegmentIconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Grid container spacing={2}>
                <Grid size={6}>
                  <HookFormInput
                    name={OrgPartFormConstants.moq.id}
                    label={OrgPartFormConstants.moq.label}
                    control={control}
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <HookFormInput
                    name={OrgPartFormConstants.multiple.id}
                    label={OrgPartFormConstants.multiple.label}
                    control={control}
                    errors={errors}
                  />
                </Grid>
              </Grid>
              {tempUoM && (
                <>
                  <Divider sx={{ my: 2 }}>SOLD IN UNITS OF</Divider>
                  <Stack direction="row" spacing={2}>
                    <HookFormNumberInput
                      name={quoteLineFormConstants.dimensionValue.id}
                      label={quoteLineFormConstants.dimensionValue.label}
                      control={control}
                      errors={errors}
                      fullWidth
                      sx={{ flex: 1 }}
                      boxSx={{ flex: 1 }}
                    />
                    <HookFormUnitOfMeasureAutocomplete
                      name={quoteLineFormConstants.dimensionUnit.id}
                      label={quoteLineFormConstants.dimensionUnit.label}
                      control={control}
                      errors={errors}
                      limitToDimensionClass={watchDimensionClass}
                    />
                    <input
                      type="hidden"
                      {...register(quoteLineFormConstants.dimensionClass.id)}
                    />
                  </Stack>
                  <Divider sx={{ my: 2 }}>AT A PRICE OF</Divider>
                </>
              )}
              <HookFormTableInput
                name={OrgPartFormConstants.prices.id}
                control={control}
                errors={errors}
                fields={[
                  {
                    component: HookFormInput,
                    name: quoteLineFormConstants.to.id,
                    label: quoteLineFormConstants.to.label,
                    props: {
                      InputProps: {
                        endAdornment: tempUoM
                          ? `${watchDimensionValue} ${
                              _.get(
                                UnitOfMeasureDetails,
                                watchDimensionUnit,
                                UnitOfMeasureOptions.PIECE
                              )?.abbreviation
                            }`
                          : 'pc',
                      },
                    },
                  },
                  {
                    component: HookFormInput,
                    name: quoteLineFormConstants.unitPrice.id,
                    label: quoteLineFormConstants.unitPrice.label,
                    props: {
                      InputProps: {
                        startAdornment: '$',
                      },
                    },
                  },
                ]}
              />
            </Stack>
          </AccordionCard>
        )}
        {formRenderMode === OrgPartType.OTS && foundPublicPart && (
          <HookFormToggleAlert
            severity="success"
            variant="standard"
            name={OrgPartFormConstants.linkMatchedPart.id}
            label={OrgPartFormConstants.linkMatchedPart.label}
            control={control}
            errors={errors}
            additionalOnChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.checked) {
                setValue(
                  OrgPartFormConstants.publicPart.id,
                  foundPublicPart.id
                );
              } else {
                setValue(OrgPartFormConstants.publicPart.id, null);
              }
            }}
          >
            Cofactr Component Cloud has an existing record for{' '}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              component="span"
              display="inline-flex"
            >
              {foundPublicPart.manufacturer
                ? `${foundPublicPart.manufacturer} `
                : ''}
              {foundPublicPart.mpn}.
              <SegmentIconButton
                tooltip="Open Part In New Tab"
                onClick={() => {
                  window.open(
                    `${window.location.origin}${ALL_APPS.PARTS.path}/${foundPublicPart.id}`,
                    '_blank'
                  );
                }}
              >
                <IconExternalLink size={16} />
              </SegmentIconButton>
            </Stack>
            <br />
            Would you like to link this part to the Component Cloud record to
            automatically keep your part data up-to-date with live specs,
            lifecycle, and supply chain data?
          </HookFormToggleAlert>
        )}
      </Stack>
    </>
  );
};

export default CreatePartForm;
